import { z } from "zod";
import { idSchema } from "./common.ts";

export const ORDER_STATUSES = ["placed", "accepted", "preparing", "ready", "served", "rejected", "cancelled"] as const;
export const orderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

/**
 * Allowed backend transitions. `rejected` is only possible before acceptance;
 * `cancelled` is possible from any non-terminal state after acceptance.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  placed: ["accepted", "rejected", "cancelled"],
  accepted: ["preparing", "ready", "cancelled"],
  preparing: ["ready", "cancelled"],
  ready: ["served", "cancelled"],
  served: [],
  rejected: [],
  cancelled: [],
};

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_TRANSITIONS[from].includes(to);
}

export function isTerminalStatus(status: OrderStatus): boolean {
  return ORDER_TRANSITIONS[status].length === 0;
}

export const orderLineInputSchema = z.object({
  itemId: idSchema,
  variantId: idSchema.nullable().optional(),
  quantity: z.number().int().min(1).max(50),
  notes: z.string().max(300).optional(),
  modifierOptionIds: z.array(idSchema).max(40).default([]),
  /** Client-displayed unit price; used only to detect stale menus, never trusted. */
  expectedUnitPriceMinor: z.number().int().optional(),
});
export const idempotencyKeySchema = z.string().min(16).max(64).regex(/^[A-Za-z0-9_-]+$/);

export const submitOrderSchema = z.object({
  menuSlug: z.string().min(1).max(64),
  tableToken: z.string().min(8).max(64),
  idempotencyKey: idempotencyKeySchema,
  language: z.string().max(16).optional(),
  notes: z.string().max(500).optional(),
  items: z.array(orderLineInputSchema).min(1).max(60),
});
export type SubmitOrderInput = z.infer<typeof submitOrderSchema>;

export const updateOrderStatusSchema = z.object({
  status: orderStatusSchema,
  reason: z.string().max(300).optional(),
});

export type OrderItemDto = {
  id: string;
  itemId: string | null;
  name: string;
  variantName: string | null;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
  notes: string | null;
  modifiers: Array<{ groupName: string; optionName: string; priceDeltaMinor: number }>;
  /** Kitchen station the line was routed to when ordered (null: no station, shown on the pass only). */
  stationId: string | null;
  stationName: string | null;
  readyAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
};

export type OrderDto = {
  id: string;
  number: number;
  restaurantId: string;
  menuId: string;
  tableId: string | null;
  tableLabel: string | null;
  status: OrderStatus;
  currency: string;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  taxInclusive: boolean;
  notes: string | null;
  language: string | null;
  source: "guest" | "pos";
  /** When the check was closed (payment is taken outside Dishfolio). */
  settledAt: string | null;
  items: OrderItemDto[];
  history: Array<{ from: OrderStatus | null; to: OrderStatus; at: string; actor: string | null; reason: string | null }>;
  createdAt: string;
  updatedAt: string;
};

export type PublicOrderDto = Omit<OrderDto, "history" | "menuId" | "restaurantId"> & {
  statusToken: string;
  restaurantName: string;
};
